import { useMemo } from "react";
import {
  Autocomplete,
  TextField,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import {
  AlertPolicyAdmin,
  useEligibleAdmins,
} from "../hooks/useAlertPolicyUsers";
import { AlertAction, AlertUser } from "../../../api/alertPolicies";

interface AdminUserPickerProps {
  value: AlertUser[];
  onChange: (users: AlertUser[]) => void;
}

const ACTIONS: AlertAction[] = [AlertAction.Message, AlertAction.Assign];

const AdminUserPicker = ({ value, onChange }: AdminUserPickerProps) => {
  const { data: admins, isLoading } = useEligibleAdmins();

  const options: AlertPolicyAdmin[] = useMemo(() => admins ?? [], [admins]);

  const selectedOptions: AlertPolicyAdmin[] = useMemo(
    () =>
      value.map(
        (user) =>
          options.find((admin) => admin.id === user.id) ?? {
            id: user.id,
            name: user.id,
            phoneNumber: "",
          },
      ),
    [value, options],
  );

  const handleSelect = (_: unknown, newValue: AlertPolicyAdmin[]) => {
    onChange(
      newValue.map(
        (admin) =>
          value.find((user) => user.id === admin.id) ?? {
            id: admin.id,
            actions: [AlertAction.Message],
          },
      ),
    );
  };

  const toggleAction = (id: string, action: AlertAction) => {
    onChange(
      value.map((user) => {
        if (user.id !== id) return user;
        const actions = user.actions.includes(action)
          ? user.actions.filter((a) => a !== action)
          : [...user.actions, action];
        return { ...user, actions };
      }),
    );
  };

  const adminFor = (id: string) => options.find((admin) => admin.id === id);

  return (
    <Stack spacing={1.5}>
      <Autocomplete
        multiple
        size="small"
        options={options}
        value={selectedOptions}
        loading={isLoading}
        getOptionLabel={(admin) =>
          admin.phoneNumber
            ? `${admin.name} (${admin.phoneNumber})`
            : admin.name
        }
        isOptionEqualToValue={(option, val) => option.id === val.id}
        onChange={handleSelect}
        noOptionsText="No admins with a phone number"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Recipients"
            placeholder="Add an admin"
          />
        )}
      />

      {value.map((user) => {
        const admin = adminFor(user.id);
        return (
          <Stack
            key={user.id}
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body2">
              {admin?.name ?? user.id}
              {admin?.phoneNumber ? ` (${admin.phoneNumber})` : ""}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {ACTIONS.map((action) => {
                const active = user.actions.includes(action);
                return (
                  <Chip
                    key={action}
                    label={action}
                    size="small"
                    color={active ? "primary" : "default"}
                    variant={active ? "filled" : "outlined"}
                    onClick={() => toggleAction(user.id, action)}
                  />
                );
              })}
            </Stack>
          </Stack>
        );
      })}

      <Typography variant="caption" color="text.secondary">
        message = SMS alert · assign = gets the ticket. The first admin with
        “assign” becomes the ticket assignee.
      </Typography>
    </Stack>
  );
};

export default AdminUserPicker;
